import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, Output } from '@angular/core';
import { SelectItem, SelectMenuComponent, SelectMode } from '../../../shared/components/select-menu/select-menu.component';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { AssetVersionPipe } from '../../../pipes/asset-version.pipe';
import { POLARIS_NAME } from '../copilot.model';
import { tooltips } from '../../../utils/tooltips';
import { DashboardService } from '../../../services/dashboard.service';
import { StateService } from '../../../services/state.service';
import { UtilService } from '../../../services/util.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import {StrategyDeckService} from './strategy-deck.service';
import { DeckResearchQuestionComponent } from './components/deck-research-question/deck-research-question.component';
import {
  DeckQuestion,
  DeckQuestionsPayload,
  DeckQuestionTopic,
  DeckQuestionTopicDisplay,
  OwnQuestionPayload
} from './strategy-deck.models';
const MAX_COMPETITORS = 5;
const MAX_QUESTIONS = 5;

function maxTotalQuestionsValidator(max: number): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const questions = group.get('questions')?.value ?? [];
    const ownQuestion = group.get('ownQuestion')?.value ?? [];
    return questions.length + ownQuestion.length > max ? { maxQuestionsExceeded: true } : null;
  };
}

export interface StrategyDeckSubmitEvent {
  brandKey: string;
  competitorKeys: string[];
  questions: DeckQuestion[];
  ownQuestion?: OwnQuestionPayload[];
  accountKey: string;
}

@Component({
  selector: 'app-strategy-deck',
  imports: [
    ReactiveFormsModule,
    SelectMenuComponent,
    AssetVersionPipe,
    MatTooltipModule,
    DeckResearchQuestionComponent,
  ],
  templateUrl: './strategy-deck.component.html',
  styleUrl: './strategy-deck.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StrategyDeckComponent {

  @Output() exitWizard = new EventEmitter<void>();
  @Output() submitWizard = new EventEmitter<StrategyDeckSubmitEvent>();

  private dashboardService = inject(DashboardService);
  private stateService = inject(StateService);
  private utilService = inject(UtilService);
  private strategyDeckService = inject(StrategyDeckService);

  protected readonly polarisName = POLARIS_NAME;
  protected readonly tooltips = tooltips.polaris;
  protected readonly SelectMode = SelectMode;

  protected readonly topicDisplay = DeckQuestionTopicDisplay;

  protected deckQuestions = toSignal(this.strategyDeckService.getDeckQuestions(), { initialValue: { questions: {}, total: 0 } as DeckQuestionsPayload });
  protected deckQuestionTopics = computed(() => Object.keys(this.deckQuestions().questions) as DeckQuestionTopic[]);

  protected readonly MAX_QUESTIONS = MAX_QUESTIONS;

  protected wizardForm: FormGroup = new FormGroup({
    brands: new FormControl<string | null>(null, [Validators.required]),
    competitors: new FormControl<string[]>([], [Validators.required, Validators.minLength(1), Validators.maxLength(MAX_COMPETITORS)]),
    questions: new FormControl<DeckQuestion[]>([], [Validators.required, Validators.minLength(1)]),
    ownQuestion: new FormControl<OwnQuestionPayload[]>([]),
    dateRange: new FormControl<string | null>(null),
  }, { validators: maxTotalQuestionsValidator(MAX_QUESTIONS) });

  protected brandControlValue = toSignal(this.wizardForm.get('brands')!.valueChanges, { initialValue: this.wizardForm.get('brands')!.value });

  protected dateRangeOptions = computed<SelectItem[]>(() => [ { label: 'Last 30 days', key: 'last_30_days' } ]);
  protected brandOptions = computed<SelectItem[]>(() => this.dashboardService.myBrandOptions() || []);
  protected competitorOptions = computed<SelectItem[]>(() => {
    const brandKey = this.brandControlValue();
    return brandKey
      ? this.dashboardService.getAllCompetitorsByBrandKey(brandKey) ?? []
      : [];
  });

  protected showSelectAllOption = computed<boolean>(() => this.competitorOptions().length <= MAX_COMPETITORS);

  protected get brandsControl(): FormControl<string | null> {
    return this.wizardForm.get('brands') as FormControl<string | null>;
  }

  protected get competitorsControl(): FormControl<string[]> {
    return this.wizardForm.get('competitors') as FormControl<string[]>;
  }

  protected get questionsControl(): FormControl<DeckQuestion[]> {
    return this.wizardForm.get('questions') as FormControl<DeckQuestion[]>;
  }

  protected get ownQuestionControl(): FormControl<OwnQuestionPayload[]> {
    return this.wizardForm.get('ownQuestion') as FormControl<OwnQuestionPayload[]>;
  }

  protected get dateRangeControl(): FormControl<string | null> {
    return this.wizardForm.get('dateRange') as FormControl<string | null>;
  }

  protected get isFormValid(): boolean {
    return this.wizardForm.valid;
  }

  protected get hasTooManyQuestions(): boolean {
    return !!this.wizardForm.errors?.['maxQuestionsExceeded'];
  }

  protected isQuestionSelected(question: DeckQuestion): boolean {
    return this.questionsControl.value.some(q => q.id === question.id);
  }

  protected onQuestionToggle(question: DeckQuestion, event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    const current = this.questionsControl.value;
    const updated = isChecked
      ? [...current, question]
      : current.filter(q => q.id !== question.id);
    this.questionsControl.setValue(updated);
  }

  protected onOwnQuestionChange(topic: DeckQuestionTopic, checked: boolean, text: string): void {
    const withoutTopic = this.ownQuestionControl.value.filter(q => q.topic !== topic);
    const updated = checked && text.trim()
      ? [...withoutTopic, { question: text.trim(), topic }]
      : withoutTopic;
    this.ownQuestionControl.setValue(updated);
  }

  protected submit(): void {
    if (this.hasTooManyQuestions) {
      this.utilService.openSnackBar(`You can select up to ${MAX_QUESTIONS} questions in total`, { variant: 'error' });
      return;
    }

    if (!this.isFormValid) {
      this.utilService.openSnackBar('Please select a brand and at least one competitor', { variant: 'error' });
      return;
    }

    const accountKey = this.stateService.activeAccount();
    this.submitWizard.emit({
      brandKey: this.brandsControl.value!,
      competitorKeys: this.competitorsControl.value,
      questions: this.questionsControl.value,
      ownQuestion: this.ownQuestionControl.value,
      accountKey,
    });
  }

  protected exit(): void {
    this.exitWizard.emit();
  }

}
